import { WEIGHTED_AMOUNT_PERCENTAGE, DEAL_STAGE_CONFIGS } from "./constants";
import type { Deal, DealsListingPayload } from "./types";
import { getDateTimeByFormat } from "../../../../../redux/slices/dateTimeFormatSlice";
import { formatToTitleCase } from "../../../../../utils/functions/formatters";
import DollarIcon from "../../../../../assets/svg/dollar-icon";
import PoundIcon from "../../../../../assets/svg/pound-icon";

// --- Currency Icon Selection ---

const DOLLAR_CURRENCIES = ["USD", "CAD"];
const DOLLAR_REGIONS = ["US", "CA"];

export const getDealCurrencyIcon = (currency?: string): typeof PoundIcon => {
  if (currency && DOLLAR_CURRENCIES.includes(currency.toUpperCase())) {
    return DollarIcon;
  }
  return PoundIcon;
};

export const getDealCurrencyIconByRegion = (
  region?: string
): typeof PoundIcon => {
  if (region && DOLLAR_REGIONS.includes(region.toUpperCase())) {
    return DollarIcon;
  }
  return PoundIcon;
};

// --- Currency Formatting ---

export const formatDealCurrency = (value: number, currency: string): string => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// --- Weighted Amount ---
export const calculateWeightedAmount = (totalAmount: number): number => {
  return totalAmount * WEIGHTED_AMOUNT_PERCENTAGE;
};

// --- Days in Current Stage (PRD logic) ---

export const getDaysInCurrentStage = (deal: Deal): number => {
  const now = Date.now();
  let referenceDate: string | null | undefined;

  switch (deal.stage) {
    case "draft":
      referenceDate = deal.tenancy_created;
      break;
    case "awaiting_signatures":
      referenceDate = deal.tenancy_sent_out;
      break;
    case "ready_to_countersign":
    case "complete":
    case "cancelled":
    case "expired":
      referenceDate = deal.current_stage_switch_time;
      break;
    default:
      referenceDate = deal.current_stage_switch_time;
      break;
  }

  if (!referenceDate) return deal.days_in_current_stage;

  const diffMs = now - new Date(referenceDate).getTime();
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
};

// --- Payload Builder ---

export const buildDealsListingPayload = (params: {
  stage?: string;
  limit?: number;
  skip?: number;
  searchQuery?: string;
  pipelineKey?: string;
  viewId?: string;
  filters?: Record<string, unknown>;
}): DealsListingPayload => {
  const payload: DealsListingPayload = {};

  if (params.stage) payload.stage = params.stage;
  if (params.limit != null) payload.limit = params.limit;
  if (params.skip != null) payload.skip = params.skip;
  if (params.searchQuery) payload.search_query = params.searchQuery;
  if (params.pipelineKey) payload.pipeline_key = params.pipelineKey;
  if (params.viewId) payload.view_id = params.viewId;
  if (params.filters && Object.keys(params.filters).length > 0) {
    payload.filters = cleanObject(params.filters);
  }

  return payload;
};

// --- Object Cleaning ---

export const cleanObject = (
  obj: Record<string, unknown>
): Record<string, unknown> => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) => value !== null && value !== undefined && value !== ""
    )
  );
};

// --- Datetime Formatting (for detail page) ---

export const formatDealDatetime = (dateStr?: string | null): string => {
  if (!dateStr) return "--";
  return new Date(dateStr).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "shortOffset",
  });
};

// --- Contract Progress Percentage ---

export const getContractProgressPercent = (
  contractStart?: string | null,
  contractEnd?: string | null
): number => {
  if (!contractStart || !contractEnd) return 0;
  const start = new Date(contractStart).getTime();
  const end = new Date(contractEnd).getTime();
  const now = Date.now();
  if (now <= start) return 0;
  if (now >= end) return 100;
  return Math.round(((now - start) / (end - start)) * 100);
};

// --- Time in Stage Label ---

export const getTimeInStageLabel = (deal: Deal): string => {
  const days = getDaysInCurrentStage(deal);
  if (days === 0) {
    const ref = deal.current_stage_switch_time || deal.tenancy_created;
    if (!ref) return "--";
    const diffMs = Date.now() - new Date(ref).getTime();
    const seconds = Math.floor(diffMs / 1000);
    if (seconds < 60) return `${seconds} seconds`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    return `${hours} hours`;
  }
  return `${days} days`;
};

export const formatDealDate = (dateStr?: string | null): string => {
  if (!dateStr) return "--";
  const { date } = getDateTimeByFormat(new Date(dateStr));
  return date || "--";
};

// --- About Deal Field Value Formatter ---

export const formatDealFieldValue = (
  value: unknown,
  type?: string,
  currency?: string
): string => {
  if (value === undefined || value === null || value === "") return "--";

  if (typeof value === "object" && value !== null && "name" in value) {
    return (value as { name: string }).name || "--";
  }

  switch (type) {
    case "date":
      return formatDealDate(String(value));
    case "datetime":
      return formatDealDatetime(String(value));
    case "currency":
      return formatDealCurrency(Number(value), currency || "GBP");
    case "boolean":
      return value ? "Yes" : "No";
    case "stage": {
      const stageConfig = DEAL_STAGE_CONFIGS.find((s) => s.stage_key === value);
      return (
        formatToTitleCase(stageConfig?.stage_title || "") ||
        formatToTitleCase(String(value))
      );
    }
    case "priority":
    default:
      return String(value);
  }
};
